<template>
  <div class="bg pb-20 p-3 mb-auto h-full overflow-hidden bg-gradient-to-b bg-gray-800 flex flex-col justify-between" id="style-1">
    <main class="flex-grow flex flex-col items-center justify-center pb-20 sm:pb-0">
      <!-- Loading / Error -->
      <div v-if="isLoading" class="flex flex-col items-center">
        <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-indigo-500 mb-3"></div>
        <p class="text-gray-300 text-sm font-zombie">กำลังค้นหาผู้รอดชีวิต...</p>
      </div>
      <div v-else-if="error" class="text-center text-red-400 text-xl font-zombie">
        {{ error }}
      </div>
      <!-- Room too low -->
      <div v-else-if="playerRoom <= 4" class="text-center flex flex-col items-center animate-fade-in">
        <i class="fas fa-skull-crossbones text-6xl text-gray-400 mb-4"></i>
        <p class="text-gray-300 text-xl font-zombie mb-2">ไม่สามารถค้นหาผู้รอดชีวิตได้</p>
        <p class="text-gray-500 text-sm font-zombie">ต้องมี 5 ห้องถึงสามารถค้นหาผู้รอดชีวิตได้</p>
      </div>
      <!-- No target yet -->
      <div v-else-if="!assassinateSurvivorUid" class="w-full max-w-md animate-fade-in">
        <!-- Control card -->
        <div class="bg-gray-900 bg-opacity-80 rounded-xl shadow-lg p-4 border border-gray-700">
          <div class="flex items-center justify-between mb-2">
            <div class="text-indigo-300 font-zombie">ค้นหาเป้าหมายในห้อง {{ getSearchRange() }}</div>
            <div class="text-xs text-gray-400">ห้องฉัน: <span class="text-indigo-300 font-bold">{{ playerRoom }}</span></div>
          </div>
          <div class="mb-2 text-xs text-gray-400 flex items-center justify-between">
            <span>
              การค้นหาวันนี้:
              <span class="font-bold text-indigo-300">{{ searchCount }}/{{ DAILY_SEARCH_LIMIT_BUILDING_1 }}</span>
              <span v-if="freeSearch > 0" class="text-green-400 ml-1"> (+{{ freeSearch }})</span>
            </span>
            <span v-if="searchCount >= DAILY_SEARCH_LIMIT_BUILDING_1 && freeSearch === 0" class="text-red-400">ครบโควตาการค้นหาวันนี้แล้ว</span>
          </div>
          <!-- Cost line -->
          <div class="mt-2 text-sm flex items-center justify-between rounded-md border text-white border-gray-700 bg-gray-800 px-3 py-2">
            <div class="flex items-center gap-2">
              <i class="fas fa-coins text-gray-300"></i><span>ค่าใช้จ่าย:</span>
              <span class="font-bold text-gray-200">{{ searchCost.amount }} silver</span>
            </div>
            <div class="text-xs" :class="isCostEnough ? 'text-green-400' : 'text-red-400'">
              {{ isCostEnough ? 'พร้อมชำระ' : 'ทรัพยากรไม่พอ' }}
            </div>
          </div>
          <div class="mt-3">
            <button
              @click="rollSurvivor"
              :disabled="isRollDisabled"
              class="w-full py-3 rounded-md text-white font-bold text-sm font-zombie transition-all duration-300 flex items-center justify-center gap-2"
              :class="isRollDisabled ? 'bg-gray-700 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99]'"
            >
              <i class="fas fa-redo"></i>
              ค้นหาผู้รอดชีวิตใหม่
              <template v-if="isSurvivorDead || isSurvivorInDifferentBuilding">
                (ฟรี - {{ isSurvivorDead ? 'เป้าหมายตาย' : 'คนละตึก' }})
              </template>
              <template v-else-if="freeSearch === 0">
                (<i :class="searchCost.icon" class="ml-1"></i> {{ searchCost.amount }})
              </template>
              <template v-else>
                (ฟรี)
              </template>
            </button>
          </div>
        </div>
        
        <!-- Buy Free Search Panel -->
        <div v-if="searchCount >= DAILY_SEARCH_LIMIT_BUILDING_1 && freeSearch === 0" 
             class="bg-gray-900 bg-opacity-80 rounded-xl shadow-lg p-4 border border-gray-700 mt-3">
          <div class="flex items-center justify-between mb-2">
            <p class="text-yellow-300 text-sm font-zombie flex items-center gap-2">
              <i class="fas fa-shopping-cart"></i>
              ซื้อค้นหาฟรี
            </p>
          </div>
          <p class="text-gray-400 text-xs mb-3">
            ซื้อค้นหาฟรี 10 ครั้ง ในราคา 20 Cash (ซื้อได้สูงสุด 1 ครั้ง/วัน ซื้อใหม่ได้หลัง 21:00 น.)
          </p>
          <div class="flex items-center justify-between text-xs text-gray-400 mb-3">
            <span>Cash ของคุณ:</span>
            <span class="font-bold text-yellow-300">{{ userCash }} Cash</span>
          </div>
          <div class="text-xs text-gray-300 mb-2">
            ซื้อวันนี้แล้ว: <span class="font-bold text-yellow-400">{{ dailyFreeSearchPurchases }}/1</span> ครั้ง
          </div>

          <!-- Warning ถ้ามี freeSearch เหลือ -->
          <div v-if="freeSearch > 0" class="text-xs text-red-400 mb-2 font-zombie animate-pulse">
            <i class="fas fa-exclamation-triangle"></i> 
            รีบใช้ค้นหาฟรีก่อน 21:00 น. ไม่งั้นจะหายทั้งหมดเมื่อรีเซ็ตเวลา 21:00!
          </div>

          <!-- แสดงเมื่อซื้อครบ 1 ครั้ง -->
          <div v-if="dailyFreeSearchPurchases >= 1" class="text-xs text-yellow-400 mb-2 font-zombie">
            <i class="fas fa-clock"></i> ซื้อครบ 1 ครั้งแล้ว สามารถซื้อใหม่ได้หลัง 21:00 น.
          </div>

          <button
            @click="buyFreeSearches"
            :disabled="isBuyingFreeSearch || userCash < 20 || dailyFreeSearchPurchases >= 1"
            class="w-full py-3 rounded-md text-white font-bold text-sm font-zombie transition-all duration-300 flex items-center justify-center gap-2"
            :class="(isBuyingFreeSearch || userCash < 20 || dailyFreeSearchPurchases >= 1) ? 'bg-gray-700 cursor-not-allowed' : 'bg-yellow-600 hover:bg-yellow-700 active:scale-[0.99]'"
          >
            <i class="fas fa-coins"></i>
            <template v-if="isBuyingFreeSearch">
              กำลังซื้อ...
            </template>
            <template v-else>
              ซื้อค้นหาฟรี 10 ครั้ง (20 Cash)
            </template>
          </button>
        </div>
      </div>
      <!-- No survivor doc in range -->
      <div v-else-if="!survivor" class="text-center flex flex-col items-center animate-fade-in">
        <i class="fas fa-skull-crossbones text-6xl text-gray-400 mb-4"></i>
        <p class="text-gray-300 text-xl font-zombie mb-2">ไม่พบผู้รอดชีวิตในช่วงห้องที่เหมาะสม</p>
        <p class="text-gray-500 text-sm font-zombie">ลองค้นหาใหม่ในห้อง {{ getSearchRange() }}</p>
        <div class="p-4 w-full max-w-md">
          <div class="bg-gray-900 bg-opacity-80 rounded-xl shadow-lg p-4 border border-gray-700">
            <div class="mb-2 text-xs text-gray-400 flex items-center justify-between">
              <span>
                การค้นหาวันนี้:
                <span class="font-bold text-indigo-300">{{ searchCount }}/{{ DAILY_SEARCH_LIMIT_BUILDING_1 }}</span>
                <span v-if="freeSearch > 0" class="text-green-400 ml-1"> (+{{ freeSearch }})</span>
              </span>
              <span v-if="searchCount >= DAILY_SEARCH_LIMIT_BUILDING_1 && freeSearch === 0" class="text-red-400">ครบโควตาการค้นหาวันนี้แล้ว</span>
            </div>
            <!-- Cost line -->
            <div class="mt-2 text-sm flex items-center justify-between rounded-md border text-white border-gray-700 bg-gray-800 px-3 py-2">
              <div class="flex items-center gap-2">
                <i class="fas fa-coins text-gray-300"></i><span>ค่าใช้จ่าย:</span>
                <span class="font-bold text-gray-200">{{ searchCost.amount }} silver</span>
              </div>
              <div class="text-xs" :class="isCostEnough ? 'text-green-400' : 'text-red-400'">
                {{ isCostEnough ? 'พร้อมชำระ' : 'ทรัพยากรไม่พอ' }}
              </div>
            </div>
            <div class="mt-3">
              <button
                @click="rollSurvivor"
                :disabled="isRollDisabled"
                class="w-full py-3 rounded-md text-white font-bold text-sm font-zombie transition-all duration-300 flex items-center justify-center gap-2"
                :class="isRollDisabled ? 'bg-gray-700 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99]'"
              >
                <i class="fas fa-redo"></i>
                ค้นหาผู้รอดชีวิตใหม่
                <template v-if="isSurvivorDead || isSurvivorInDifferentBuilding">
                  (ฟรี - {{ isSurvivorDead ? 'เป้าหมายตาย' : 'คนละตึก' }})
                </template>
                <template v-else-if="freeSearch === 0">
                  (<i :class="searchCost.icon" class="ml-1"></i> {{ searchCost.amount }})
                </template>
                <template v-else>
                  (ฟรี)
                </template>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Found but dead or in different building -->
      <div v-else-if="survivor && (!survivor.isLive || isSurvivorInDifferentBuilding)" class="text-center flex flex-col items-center animate-fade-in">
        <i class="fas fa-skull-crossbones text-6xl text-gray-400 mb-4"></i>
        <p class="text-gray-300 text-xl font-zombie mb-2">{{ survivor.isLive ? 'เป้าหมายอยู่ในตึกอื่น' : 'ผู้รอดชีวิตนี้ตายแล้ว' }} กรุณาค้นหาใหม่</p>
        <p class="text-gray-500 text-sm font-zombie">ลองค้นหาใหม่ในห้อง {{ getSearchRange() }}</p>
        <div class="p-4 w-full max-w-md">
          <div class="bg-gray-900 bg-opacity-80 rounded-xl shadow-lg p-4 border border-gray-700">
            <div class="mb-2 text-xs text-gray-400 flex items-center justify-between">
              <span>
                การค้นหาวันนี้:
                <span class="font-bold text-indigo-300">{{ searchCount }}/{{ DAILY_SEARCH_LIMIT_BUILDING_1 }}</span>
                <span v-if="freeSearch > 0" class="text-green-400 ml-1"> (+{{ freeSearch }})</span>
              </span>
            </div>
            <!-- Cost line -->
            <div class="mt-2 text-sm flex items-center justify-between rounded-md border text-white border-gray-700 bg-gray-800 px-3 py-2">
              <div class="flex items-center gap-2">
                <i class="fas fa-coins text-gray-300"></i><span>ค่าใช้จ่าย:</span>
                <span class="font-bold text-gray-200">{{ searchCost.amount }} silver</span>
              </div>
              <div class="text-xs" :class="isCostEnough ? 'text-green-400' : 'text-red-400'">
                {{ isCostEnough ? 'พร้อมชำระ' : 'ทรัพยากรไม่พอ' }}
              </div>
            </div>
            <div class="mt-3">
              <button
                @click="rollSurvivor"
                :disabled="isRollDisabled"
                class="w-full py-3 rounded-md text-white font-bold text-sm font-zombie transition-all duration-300 flex items-center justify-center gap-2"
                :class="isRollDisabled ? 'bg-gray-700 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99]'"
              >
                <i class="fas fa-redo"></i>
                ค้นหาผู้รอดชีวิตใหม่
                <span>(ฟรี - {{ isSurvivorDead ? 'เป้าหมายตาย' : 'คนละตึก' }})</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Survivor card -->
      <div v-else class="w-full max-w-md">
        <div class="zombie-card bg-gray-900 bg-opacity-80 rounded-xl shadow-lg overflow-hidden border border-gray-700 cursor-pointer" @click="openModal(survivor.docId)">
          <div class="relative">
            <img :src="displayImage" alt="Survivor Image" class="w-full h-80 object-cover" />
            <!-- Refresh (บนซ้าย) -->
            <button
              @click.stop="rollSurvivor"
              :disabled="isRollDisabled"
              class="absolute top-2 left-2 p-2 rounded-full transition-all duration-300 border"
              :class="isRollDisabled ? 'bg-gray-700 border-gray-700 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 border-indigo-500 text-white'"
              :title="`ค้นหาใหม่ (${isSurvivorDead ? 'ฟรี - เป้าหมายตาย' : isSurvivorInDifferentBuilding ? 'ฟรี - คนละตึก' : freeSearch > 0 ? 'ฟรี' : `${searchCost.amount} silver`})`"
            >
              <i class="fas fa-redo"></i>
            </button>
            <!-- Room & Level (บน) -->
            <div class="absolute top-2 left-1/2 -translate-x-1/2 bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold font-zombie">
              ห้อง {{ survivor.room }}
            </div>
            <div class="absolute top-2 right-2 bg-purple-600 text-white px-2 py-1 rounded-full text-xs font-zombie font-bold">
              LV {{ survivor.level }}
            </div>
          </div>
          <!-- Card content -->
          <div class="p-4">
            <div class="flex items-center justify-between mb-2">
              <h3 class="text-xl font-bold text-purple-300 font-zombie truncate">{{ displayName }}</h3>
            </div>
            <div class="mb-2 text-xs text-gray-400 flex items-center justify-between">
              <span>
                การค้นหาวันนี้:
                <span class="font-bold text-indigo-300">{{ searchCount }}/{{ DAILY_SEARCH_LIMIT_BUILDING_1 }}</span>
                <span v-if="freeSearch > 0" class="text-green-400 ml-1"> (+{{ freeSearch }})</span>
              </span>
              <span v-if="searchCount >= DAILY_SEARCH_LIMIT_BUILDING_1 && freeSearch === 0" class="text-red-400">ครบโควตาการค้นหาวันนี้แล้ว</span>
            </div>
            <div v-if="assassinationAttempts < 3" class="text-red-400 text-xs mb-2 font-zombie">
              ลอบสังหารให้ครบ 3 ครั้ง
            </div>

            <!-- ปุ่มผ่าน (เฉพาะเมื่อมีเป้าหมายที่ต้องจัดการจริง ๆ) -->
            <div v-if="survivor && survivor.isLive && !isSurvivorInDifferentBuilding" class="mt-3">
              <button
                @click.stop="passSurvivor"
                :disabled="isPassing || dailyPassCount >= 1"
                class="w-full py-3 rounded-md text-white font-bold text-sm font-zombie transition-all duration-300 flex items-center justify-center gap-2"
                :class=" (isPassing || dailyPassCount >= 1) ? 'bg-gray-700 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700 active:scale-[0.99]' "
              >
                <i class="fas fa-forward"></i>
                <template v-if="isPassing">
                  กำลังผ่าน...
                </template>
                <template v-else-if="dailyPassCount >= 1">
                  ผ่านวันนี้แล้ว (จะผ่านได้อีกครั้งหลัง 21:00)
                </template>
                <template v-else>
                  ผ่านเป้าหมายนี้ (ใช้เมื่อเป้าหมายตั้งร้านค้าหรือติดอีเว้น)
                </template>
              </button>
            </div>

            <!-- Roll Button -->
            <div class="mt-3">
              <button
                @click.stop="rollSurvivor"
                :disabled="isRollDisabled"
                class="w-full py-3 rounded-md text-white font-bold text-sm font-zombie transition-all duration-300 flex items-center justify-center gap-2"
                :class="isRollDisabled ? 'bg-gray-700 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99]'"
              >
                <i class="fas fa-redo"></i>
                ค้นหาผู้รอดชีวิตใหม่
                <template v-if="isSurvivorDead || isSurvivorInDifferentBuilding">
                  (ฟรี - {{ isSurvivorDead ? 'เป้าหมายตาย' : 'คนละตึก' }})
                </template>
                <template v-else-if="freeSearch === 0">
                  (<i :class="searchCost.icon" class="ml-1"></i> {{ searchCost.amount }})
                </template>
                <template v-else>
                  (ฟรี)
                </template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
    <SurvivorModal v-if="showModal" :survivor-id="selectedSurvivorId" @close="closeModal" />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { projectFirestore, projectAuth, projectFunctions } from '@/firebase/config';
import { usePlayerStore } from '@/composables/usePlayerStore';
import SurvivorModal from '../components/SurvivorModal.vue';

export default {
  name: 'Survivors',
  components: { SurvivorModal },
  setup() {
    const DAILY_SEARCH_LIMIT_BUILDING_1 = 10;

    const survivor = ref(null);
    const character = ref({});
    const assassinateSurvivorUid = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    const showModal = ref(false);
    const selectedSurvivorId = ref(null);
    const playerRoom = ref(null);
    const userId = ref(null);
    const searchCount = ref(0);
    const freeSearch = ref(0);
    const assassinationAttempts = ref(0);
    const userCash = ref(0);
    const dailyFreeSearchPurchases = ref(0);
    const dailyPassCount = ref(0); // เพิ่มสำหรับฟังก์ชันผ่าน
    const isBuyingFreeSearch = ref(false);
    const isPassing = ref(false); // สำหรับ loading
    
    const playerStore = usePlayerStore();
    let unsubscribeSurvivor = null;

    const anonymousImage = 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2Fanonymouse.png?alt=media&token=25259b2d-03f1-49fb-b70c-054cd88d2a86';

    const roomRanges = [
      { playerRoom: 2, searchRooms: [] },
      { playerRoom: 3, searchRooms: [] },
      { playerRoom: 4, searchRooms: [] },
      { playerRoom: 5, searchRooms: [5] },
      { playerRoom: 6, searchRooms: [6, 7, 8, 9, 10] },
      { playerRoom: 7, searchRooms: [6, 7, 8, 9, 10, 11] },
      { playerRoom: 8, searchRooms: [6, 7, 8, 9, 10, 11, 12] },
      { playerRoom: 9, searchRooms: [6, 7, 8, 9, 10, 11, 12] },
      { playerRoom: 10, searchRooms: [6, 7, 8, 9, 10, 11, 12] },
      { playerRoom: 11, searchRooms: [7, 8, 9, 10, 11, 12] },
      { playerRoom: 12, searchRooms: [8, 9, 10, 11, 12] },
    ];

    const getSearchRange = () => {
      if (playerRoom.value === 1) return 'ไม่สามารถค้นหาได้';
      const specificRange = roomRanges.find(range => range.playerRoom === playerRoom.value);
      if (specificRange) {
        const rooms = specificRange.searchRooms;
        if (rooms.length === 1) return `${rooms[0]}`;
        if (rooms.length > 1) return `${rooms[0]} ถึง ${rooms[rooms.length - 1]}`;
        return 'ไม่สามารถค้นหาได้';
      }
      return 'ไม่สามารถค้นหาได้';
    };

    const costMap = {
      5: { type: 'silver', amount: 300, icon: 'fas fa-coins' },
      6: { type: 'silver', amount: 300, icon: 'fas fa-coins' },
      7: { type: 'silver', amount: 300, icon: 'fas fa-coins' },
      8: { type: 'silver', amount: 300, icon: 'fas fa-coins' },
      9: { type: 'silver', amount: 300, icon: 'fas fa-coins' },
      10: { type: 'silver', amount: 300, icon: 'fas fa-coins' },
    };

    const searchCost = computed(() => {
      if (isSurvivorDead.value || isSurvivorInDifferentBuilding.value) return { type: 'none', amount: 0, icon: '' };
      const r = playerRoom.value;
      if (!r) return { type: 'silver', amount: 300, icon: 'fas fa-coins' };
      return costMap[r] || { type: 'silver', amount: 300, icon: 'fas fa-coins' };
    });

    const isCostEnough = computed(() => {
      if (freeSearch.value > 0 || isSurvivorDead.value || isSurvivorInDifferentBuilding.value) return true;
      return (character.value.silver || 0) >= searchCost.value.amount;
    });

    const isSurvivorDead = computed(() => {
      return survivor.value && !survivor.value.isLive;
    });

    const isSurvivorInDifferentBuilding = computed(() => {
      if (!survivor.value || !character.value.building) return false;
      return Number(survivor.value.building) !== Number(character.value.building);
    });

    const isRollDisabled = computed(() => {
      return isLoading.value ||
        !isCostEnough.value ||
        playerRoom.value === 1 ||
        (searchCount.value >= DAILY_SEARCH_LIMIT_BUILDING_1 && freeSearch.value === 0 && !isSurvivorDead.value && !isSurvivorInDifferentBuilding.value) ||
        (assassinateSurvivorUid.value && assassinationAttempts.value < 3 && survivor.value && survivor.value.isLive && !isSurvivorInDifferentBuilding.value);
    });

    const displayName = computed(() => {
      if (!survivor.value || character.value.building === 1 || character.value.building === 2) return 'บุคคลลึกลับ';
      return survivor.value.characterName;
    });

    const displayImage = computed(() => {
      if (!survivor.value || character.value.building === 1 || character.value.building === 2) return anonymousImage;
      return survivor.value.imageUrl;
    });

    const fetchRandomSurvivor = async () => {
      if (playerRoom.value === 1) { error.value = 'ไม่สามารถค้นหาผู้รอดชีวิตจากห้อง 1'; return; }
      if (assassinateSurvivorUid.value && survivor.value && survivor.value.isLive && !isSurvivorInDifferentBuilding.value && assassinationAttempts.value < 3) {
        error.value = 'ลอบสังหารให้ครบ 3 ครั้ง';
        return;
      }
      if (!isSurvivorDead.value && !isSurvivorInDifferentBuilding.value && assassinateSurvivorUid.value) {
        if (searchCount.value >= DAILY_SEARCH_LIMIT_BUILDING_1 && freeSearch.value === 0) {
          error.value = `ครบโควตาการค้นหาวันนี้แล้ว (${DAILY_SEARCH_LIMIT_BUILDING_1} ครั้ง)`;
          return;
        }
      }
      isLoading.value = true;
      error.value = null;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'findRandomSurvivor3' });
        if (result.data.success && result.data.survivorId) {
          assassinateSurvivorUid.value = result.data.survivorId;
        } else {
          survivor.value = null;
          assassinateSurvivorUid.value = null;
        }
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการค้นหาผู้รอดชีวิต: ' + (err.message || '');
        console.error(err);
      } finally {
        isLoading.value = false;
      }
    };

    const rollSurvivor = async () => { await fetchRandomSurvivor(); };

    const buyFreeSearches = async () => {
      if (isBuyingFreeSearch.value) return;
      
      isBuyingFreeSearch.value = true;
      error.value = null;
      
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'buyFreeSearches' });
        
        if (result.data.success) {
          console.log(result.data.message);
        }
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการซื้อค้นหาฟรี';
        console.error(err);
      } finally {
        isBuyingFreeSearch.value = false;
      }
    };

    // ฟังก์ชันผ่านใหม่
    const passSurvivor = async () => {
      if (isPassing.value) return;
      isPassing.value = true;
      error.value = null;

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'passCurrentSurvivor' });
        if (result.data.success) {
          // Backend จะล้าง assassinateSurvivorUid และ +1 freeSearch ให้อัตโนมัติ
          console.log('ผ่านเป้าหมายสำเร็จ ได้รับค้นหาฟรี 1 ครั้ง');
        }
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการผ่านเป้าหมาย';
        console.error(err);
      } finally {
        isPassing.value = false;
      }
    };

    const openModal = (docId) => {
      if (docId === userId.value) return;
      selectedSurvivorId.value = docId;
      showModal.value = true;
    };

    const closeModal = () => {
      showModal.value = false;
      selectedSurvivorId.value = null;
    };

    watch(() => playerStore.userData, (data) => {
      userCash.value = data?.cash || 0;
    }, { immediate: true });

    watch(() => playerStore.characterData, (data) => {
      if (data) {
        character.value = { ...character.value, ...data };
        playerRoom.value = data.room || 2;
      } else {
        character.value = {};
        playerRoom.value = 2;
        error.value = 'ไม่พบข้อมูลผู้เล่น';
      }
    }, { immediate: true });

    const setupSurvivorListener = (uid) => {
      if (!uid) return;
      if (unsubscribeSurvivor) { unsubscribeSurvivor(); unsubscribeSurvivor = null; }
      unsubscribeSurvivor = projectFirestore.collection('characters').doc(uid).onSnapshot(snap => {
        if (snap.exists) {
          const sd = snap.data();
          survivor.value = {
            docId: snap.id,
            characterName: sd.characterName,
            level: sd.level,
            room: sd.room,
            imageUrl: sd.imageUrl,
            silver: sd.silver,
            gold: sd.gold,
            diamond: sd.diamond,
            isLive: sd.isLive,
            building: sd.building
          };
        } else {
          survivor.value = { isLive: false };
          assassinateSurvivorUid.value = null;
        }
      }, err => {
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลผู้รอดชีวิต: ' + err.message;
        survivor.value = { isLive: false };
        assassinateSurvivorUid.value = null;
      });
    };

    watch(() => playerStore.characterPrivateData, (data) => {
      if (data) {
        assassinateSurvivorUid.value = data.assassinateSurvivorUid || null;
        searchCount.value = data.searchCount || 0;
        freeSearch.value = data.freeSearch || 0;
        assassinationAttempts.value = data.assassinationAttempts || 0;
        dailyFreeSearchPurchases.value = data.dailyFreeSearchPurchases || 0;
        dailyPassCount.value = data.dailyPassCount || 0;
        
        if (assassinateSurvivorUid.value && (!unsubscribeSurvivor || survivor.value?.docId !== assassinateSurvivorUid.value)) {
          setupSurvivorListener(assassinateSurvivorUid.value);
        }
      } else {
        assassinateSurvivorUid.value = null;
        survivor.value = null;
        searchCount.value = 0;
        freeSearch.value = 0;
        assassinationAttempts.value = 0;
        dailyFreeSearchPurchases.value = 0;
        dailyPassCount.value = 0;
        if (unsubscribeSurvivor) { unsubscribeSurvivor(); unsubscribeSurvivor = null; }
      }
    }, { immediate: true });

    const setupRealtimeListeners = async () => {
      try {
        const user = projectAuth.currentUser;
        if (!user) throw new Error('ผู้ใช้ไม่ได้ล็อกอิน');
        userId.value = user.uid;
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการตั้งค่าการฟังข้อมูล: ' + err.message;
      }
    };

    onMounted(setupRealtimeListeners);

    onUnmounted(() => {
      if (unsubscribeSurvivor) unsubscribeSurvivor();
    });

    return {
      DAILY_SEARCH_LIMIT_BUILDING_1,
      survivor,
      character,
      assassinateSurvivorUid,
      isLoading,
      error,
      showModal,
      selectedSurvivorId,
      playerRoom,
      userId,
      searchCount,
      freeSearch,
      assassinationAttempts,
      userCash,
      dailyFreeSearchPurchases,
      dailyPassCount,
      isBuyingFreeSearch,
      isPassing,
      getSearchRange,
      rollSurvivor,
      buyFreeSearches,
      passSurvivor,
      openModal,
      closeModal,
      searchCost,
      isCostEnough,
      isSurvivorDead,
      isSurvivorInDifferentBuilding,
      isRollDisabled,
      displayName,
      displayImage
    };
  }
};
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2F%E0%B8%AB%E0%B9%89%E0%B8%AD%E0%B8%87%E0%B9%82%E0%B8%96%E0%B8%87.png?alt=media&token=09a147d0-386c-4f34-8f4a-3165ae99e756') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(10,10,10,.95), rgba(20,20,20,.98));
}
@font-face { font-family: 'Zombie'; src: url('https://fonts.googleapis.com/css2?family=Creepster&display=swap'); }
.font-zombie { font-family: 'Zombie', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial; }
.zombie-card { border: 1px solid rgba(75,94,154,.3); animation: slideIn .5s ease-out; }
.zombie-card:hover { border-color: rgba(75,94,154,.8); box-shadow: 0 0 15px rgba(75,94,154,.4); }
@keyframes slideIn { from { opacity: 0; transform: translateY(100%); } to { opacity: 1; transform: translateY(0); } }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fade-in { animation: fadeIn .5s ease-out; }
@media (hover: none) {
  .zombie-card button { padding: .75rem .9rem; }
}
</style>